import { Dispatch, SetStateAction } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Stack } from '@mui/system';
import { CompanyDto } from 'tdc-web-backend/companies/schemas';
import { useTranslation } from 'react-i18next';
import ConfirmModal from '../../../../../../../../../../components/confirm-modal/ConfirmModal';
import { ReactComponent as SuitcaseCheckIcon } from '../../../../../../../../../../assets/icons/layout-icons/SuitcaseCheckIcon.svg';
import { primaryLight } from '../../../../../../../../../../utils/color';
import { useRefresh } from '../../../../../../../../../../utils/hooks/crud-hooks/useRefresh';
import CustomInput from '../../../../../../../../../../components/custom-input/CustomInput';
import CustomChip from '../../../../../../../../../../components/custom-chip/CustomChip';
import { ReactComponent as PlusIcon } from '../../../../../../../../../../assets/icons/button-icons/PlusIcon.svg';
import CustomButton from '../../../../../../../../../../components/button/CustomButton';
import useUserCompany from '../../../../../../../../../../utils/hooks/useUserCompany';

interface CompanyAwardsEditModalProps {
  isEditModalOpen: boolean;
  setIsEditModalOpen: Dispatch<SetStateAction<boolean>>;
}

interface FormValues extends Pick<CompanyDto, 'awards'> {
  newAward: string;
}

const CompanyAwardsEditModal = ({
  isEditModalOpen,
  setIsEditModalOpen,
}: CompanyAwardsEditModalProps) => {
  const { company: companyData, updateCompany, isLoadingUpdateCompany } = useUserCompany();
  const refresh = useRefresh();
  const formMethods = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
      awards: companyData?.awards || [],
      newAward: '',
    },
  });
  const { t } = useTranslation();
  const { handleSubmit, setValue, getValues, watch } = formMethods;

  const awards = watch('awards') || [];
  const newAwardValue = watch('newAward');

  const handleAddAward = () => {
    const newAward = getValues('newAward') || '';
    const awards = getValues('awards') || [];
    if (awards.includes(newAward.trim())) {
      formMethods.setError('newAward', { type: 'manual', message: 'Award already added!' });
      return;
    }
    const updatedAwards = [...awards, newAward.trim()];
    setValue('awards', updatedAwards);
    setValue('newAward', '');
  };

  const handleRemoveAward = (index: number) => {
    const awards = getValues('awards') || [];
    const updatedAwards = awards.filter((_, i) => i !== index);
    setValue('awards', updatedAwards);
  };

  const onSubmit = (data: FormValues) => {
    updateCompany(
      { awards: data.awards },
      {
        onSuccess: () => {
          refresh();
          setIsEditModalOpen(false);
        },
      },
    );
  };

  return (
    <ConfirmModal
      width="40%"
      confirmButtonText={t('common.save')}
      onCancel={() => setIsEditModalOpen(false)}
      title={t('settings.companyProfile.awards')}
      isModalOpen={isEditModalOpen}
      setIsModalOpen={setIsEditModalOpen}
      iconProps={{
        icon: <SuitcaseCheckIcon fill={primaryLight[500]} />,
        wrapperBackgroundColor: primaryLight[100],
      }}
      onConfirm={handleSubmit(onSubmit)}
      automaticallyCloseModalOnButtonClick={false}
      isLoading={isLoadingUpdateCompany}
      message={
        <FormProvider {...formMethods}>
          <Stack direction="column" gap={1}>
            <CustomInput
              name="newAward"
              input={<CustomInput.Text />}
              placeholder={t('common.type')}
              label={t('settings.companyProfile.awardName')}
              sx={{ minHeight: 'none' }}
            />
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {awards.map((award: string, index: number) => (
                <CustomChip
                  key={`${award}-${index}`}
                  label={award}
                  onDelete={() => handleRemoveAward(index)}
                />
              ))}
            </Stack>

            <CustomButton
              variant="primaryLight"
              sx={{
                alignSelf: 'flex-end',
              }}
              disabled={!newAwardValue || newAwardValue.trim() === ''}
              onClick={handleAddAward}
              startIcon={<PlusIcon />}
            >
              {t('settings.companyProfile.addAward')}
            </CustomButton>
          </Stack>
        </FormProvider>
      }
    />
  );
};

export default CompanyAwardsEditModal;
